import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AccountTreeTwoTone,
  ArrowBack,
  CategoryTwoTone,
  DashboardTwoTone,
  EuroTwoTone,
  GroupsTwoTone,
  PersonAddTwoTone,
  PersonAddTwoTone as RegistrationsIcon,
  ShoppingCartTwoTone,
  TouchAppTwoTone,
  TrendingUpTwoTone,
} from '@mui/icons-material';
import { ResponsivePie } from '@nivo/pie';
import { tokens } from '../../../theme';
import {
  BuyerStatusSummary,
  CUSTOMER_STATUS_LABELS,
  EvaluationMetrics,
  MappingBreakdownItem,
  RoomRetentionData,
  WebinarComparisonData,
  WebinarEngagementDetailData,
  WebinarGroupedEvaluationDetailData,
  WebinarMappingBreakdownData,
  WebinarOrderDetailData,
  WebinarRegistrationData,
} from '../../../helpers/webinarStatisticsData';
import { formatGermanTime } from '../../../helpers/germanTime';
import {
  CATEGORY_HINTS,
  KPI,
  KPI_COLORS,
  KPI_CONCEPTS,
  TERMS,
  formatKpi,
} from '../../../helpers/webinarKpiDefinitions';
import { formatEur, formatPct } from '../../../helpers/kpiDefinitions';
import Header from '../../../components/Header';
import Explainer from '../../../components/Explainer';
import InfoTip from '../../../components/InfoTip';
import KpiTile from '../../../components/auswertungen/KpiTile';
import DeviceFunnel from '../../../components/auswertungen/DeviceFunnel';
import GroupedSections from '../../../components/auswertungen/GroupedSections';
import MetricsTable, { ExtraColumn } from '../../../components/auswertungen/MetricsTable';
import RoomRetentionChart from '../../../components/auswertungen/RoomRetentionChart';
import WebinarComparison from '../../../components/auswertungen/WebinarComparison';
import BezugsartTabelle from '../../../components/auswertungen/BezugsartTabelle';
import SortableTable, { Spalte } from '../../../components/auswertungen/SortableTable';
import type { BezugsartAntwort, DetailKennzahlen, ShowUpAntwort, TypischDetail, TypischVorTermin } from '../../../helpers/auswertungenData';
import SektionHerkunft from './SektionHerkunft';
import { GegenTypisch } from '../webinare-produkte/shared';
import { abstandTypisch } from '../webinare-produkte/befunde';
import { SHOWUP_GRUND } from '../webinare-produkte/shared';
import { BezahltBuendel } from '../../../components/auswertungen/kennzahlTeile';
import { formatNumber } from '../../../helpers/numberUtils';
import SektionKopf from './SektionKopf';
import { breiteAus } from '../../../components/auswertungen/spaltenwahl';
import { ENG_UNTER, useBreitePx } from '../../../components/auswertungen/kennzahlTeile';

/** Wie eine Bestellung dem Webinar zugeordnet wurde — in Worten fuer Marketing. */
// `kurz`: Beschriftung im Ringdiagramm, wo der volle Name abgeschnitten wuerde (Tabelle daneben zeigt `label`).
const MAPPING_METHODS: Record<string, { label: string; kurz?: string; tooltip: string; color: string }> = {
  rid: {
    label: 'RID',
    tooltip: 'Die persönliche Kennung im Bestelllink traf eine Anmeldung zu diesem Webinar.',
    color: '#2e7d32',
  },
  banner_webinar_id: {
    label: 'Banner',
    tooltip: 'Die Webinar-Kennung stand im Banner- bzw. Link-Code der Bestellung.',
    color: '#43a047',
  },
  email_banner: {
    label: 'E-Mail + Banner',
    tooltip: 'Gleiche E-Mail wie bei der Anmeldung, dazu ein passender Banner-Code.',
    color: '#66bb6a',
  },
  email_doi_url: {
    label: 'E-Mail + DOI',
    tooltip: 'Gleiche E-Mail wie bei der Anmeldung, dazu die Bestätigungsseite des Webinars.',
    color: '#81c784',
  },
  email_affiliate: {
    label: 'E-Mail + Affiliate',
    tooltip: 'Gleiche E-Mail wie bei der Anmeldung, dazu ein passender Affiliate-Code.',
    color: '#1e88e5',
  },
  affiliate_concept: {
    label: 'Affiliate-Konzept',
    tooltip:
      'Nur über das Produktkonzept im Affiliate-Link zugeordnet — dazu gibt es keine Anmeldung.',
    color: '#8e24aa',
  },
  // Paket F (Till 30.10.2026): kommt nur aus der Sektions-Sicht (/api/auswertungen), nie von der alten Seite.
  registration_no_banner: {
    label: 'über Anmeldung (ohne Kennung)',
    kurz: 'über Anmeldung',
    tooltip:
      'Im Bestell-Link stand keine Kennung. Die Bestellung zählt trotzdem hier, weil dieselbe E-Mail-Adresse vorher zu diesem Webinar angemeldet war und höchstens 72 Stunden nach Beginn bestellt hat.',
    color: '#00897b',
  },
  unmapped: { label: 'Nicht zugeordnet', tooltip: 'Keine Zuordnung.', color: '#e53935' },
};

const methodChip = (method: string, extra?: string) => {
  const m = MAPPING_METHODS[method] ?? { label: method, tooltip: method, color: '#757575' };
  return (
    <Tooltip title={extra ? `${m.tooltip}\n${extra}` : m.tooltip} arrow>
      <Chip
        label={m.label}
        size="small"
        sx={{ backgroundColor: m.color, color: 'white', fontWeight: 'bold', cursor: 'help' }}
      />
    </Tooltip>
  );
};

const yesNo = (v: number | null | undefined) =>
  v === null || v === undefined ? '–' : v ? 'Ja' : 'Nein';

/** Einfache sortierbare Liste fuer die Zeilen-Tabs (Anfos, Registrierungen). */
function useSortedRows<T>(rows: T[], initialBy: string) {
  const [by, setBy] = useState(initialBy);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const va = (a as Record<string, unknown>)[by];
        const vb = (b as Record<string, unknown>)[by];
        if (va === null || va === undefined) return 1;
        if (vb === null || vb === undefined) return -1;
        const cmp =
          typeof va === 'number' && typeof vb === 'number'
            ? va - vb
            : String(va).localeCompare(String(vb), 'de');
        return order === 'asc' ? cmp : -cmp;
      }),
    [rows, by, order],
  );
  const head = (id: string, label: string, tooltip?: string, color?: string) => (
    <TableCell
      key={id}
      sx={{
        color: color ?? 'black',
        fontWeight: 'bold',
        backgroundColor: '#e0e0e0 !important',
        whiteSpace: 'nowrap',
      }}
    >
      <TableSortLabel
        active={by === id}
        direction={by === id ? order : 'asc'}
        onClick={() => {
          if (by === id) setOrder(order === 'asc' ? 'desc' : 'asc');
          else {
            setBy(id);
            setOrder('desc');
          }
        }}
      >
        {label}
      </TableSortLabel>
      {tooltip && <InfoTip text={tooltip} />}
    </TableCell>
  );
  return { sorted, head };
}

const CELL = { color: 'black', whiteSpace: 'nowrap' } as const;

/**
 * „4 Erstbesteller“ / „1 früher gezahlt“ — Anzahl vor dem Wortlaut der Kundentypen (TERMS). „Erstbesteller“
 * ist Ein- und Mehrzahl zugleich; die übrigen Labels sind Beschreibungen und bleiben gleich, nur klein.
 */
const buyerStatusCount = (status: keyof typeof CUSTOMER_STATUS_LABELS, n: number): string => {
  const zahl = n.toLocaleString('de-DE');
  const label = CUSTOMER_STATUS_LABELS[status];
  return status === 'neu' ? `${zahl} ${label}` : `${zahl} ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
};

/** Käufer nach Kundenstatus vor ihrer ersten Anfo dieses Webinars (Personen, nicht Anfos). */
const BuyerStatusLine: FC<{ status: BuyerStatusSummary }> = ({ status }) => {
  const parts: Array<[keyof typeof CUSTOMER_STATUS_LABELS, number]> = [
    ['neu', status.neu],
    ['bestellt_unbezahlt', status.bestellt_unbezahlt],
    ['zahlend', status.zahlend],
    ['ohne_adresse', status.ohne_adresse],
  ];
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
        {status.buyers.toLocaleString('de-DE')} Käufer
        <InfoTip text="Je Käufer (Person) einmal gezählt, eingeordnet nach der ersten Anfo dieses Webinars: Erstbesteller, früher bestellt und nie gezahlt, oder früher gezahlt. Maßgeblich sind frühere Bestellungen derselben Adresse (Bestell- oder Anmelde-E-Mail), mindestens 2 h vorher, ohne Stornos und Testbestellungen. „Früher gezahlt“ = hat für eine frühere Bestellung bis heute gezahlt. Dieselben Kundentypen wie in „Anfos & Umsatz“." />
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {parts
          .filter(([, v]) => v > 0)
          .map(([k, v]) => (
            <Chip
              key={k}
              label={buyerStatusCount(k, v)}
              sx={{ backgroundColor: '#eef4fc', color: 'black', fontWeight: 600 }}
            />
          ))}
      </Box>
    </Box>
  );
};

/** Unter so vielen eingeordneten Käufern ist der Anteil grau (eine Handvoll Personen entscheidet, Leitregel 4). */
const MIN_KAEUFER = 20;

/**
 * Käufer nach Kundentyp, Sektion (Persona-Umbau B9): Hauptwert ist der Anteil „früher bestellt, nie gezahlt" gegen den
 * Schnitt der Sektion in den 12 Monaten VOR dem Termin (`fetchTypischVorTermin`, Plan 2026-10-30-vergleichsfenster-drift;
 * bis dahin die 12 Monate vor heute); die Anzahlen als Chips darunter. Ohne Schnitt (unter der Grau-Schwelle, Abruf
 * scheitert) steht der Anteil allein.
 */
const SektionKaeufer: FC<{ status: BuyerStatusSummary; schnitt: number | null }> = ({ status, schnitt }) => {
  const eingeordnet = status.buyers - status.ohne_adresse;
  const anteil = eingeordnet > 0 ? (status.bestellt_unbezahlt * 100) / eingeordnet : null;
  const wenig = eingeordnet < MIN_KAEUFER;
  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
        {TERMS.nieGezahltAnteil.label}
        <InfoTip text={TERMS.nieGezahltAnteil.tip} />
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, color: wenig ? 'text.disabled' : 'black' }}>
        {anteil === null ? '—' : formatPct(anteil, 0)}
      </Typography>
      <Typography variant="body2" component="div" sx={{ color: '#555', fontSize: 12 }}>
        {status.bestellt_unbezahlt.toLocaleString('de-DE')} von {eingeordnet.toLocaleString('de-DE')} eingeordneten Käufern
        {wenig && ` — unter ${MIN_KAEUFER} Käufern, der Anteil hängt an Einzelnen`}
        {anteil !== null && schnitt !== null && (
          // Mehr „nie gezahlt" ist schlechter — der Pfeil zeigt die Richtung, die Farbe bleibt neutral (kein Gut/Schlecht-Urteil).
          <GegenTypisch abstand={abstandTypisch(anteil, schnitt, 'pp')} wort="Schnitt" typischText={`${formatPct(schnitt, 0)} (Sektion, 12 Monate vor dem Termin)`} neutral tip={TERMS.nieGezahltAnteil.tip} />
        )}
      </Typography>
      <Box sx={{ mt: 1.5 }}>
        <BuyerStatusLine status={status} />
      </Box>
    </Box>
  );
};

/** Vertragsstatus in Worten (Sektion, P1-V3 § 4: „Status L/B/W → läuft / beendet / Widerruf"). */
const STATUS_WORT: Record<string, string> = { L: 'läuft', B: 'beendet', W: 'Widerruf' };

const OrdersTab: FC<{ rows: WebinarOrderDetailData[]; totals: EvaluationMetrics; sektion?: boolean; d?: DetailKennzahlen | null }> = ({
  rows,
  totals,
  sektion = false,
  d = null,
}) => {
  const { sorted, head } = useSortedRows(rows, 'created_on');
  const laufen = rows.filter((o) => o.vertrag_sts === 'L').length;
  const widerrufen = rows.filter((o) => o.vertrag_sts === 'W').length;
  return (
    <Box>
      {sektion && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>
            {laufen.toLocaleString('de-DE')} laufen · {widerrufen.toLocaleString('de-DE')} widerrufen
          </strong>
          <InfoTip text="Widerrufe sind 2026 normal (etwa die Hälfte der Anfos) — deshalb keine eigene Kachel." />
        </Typography>
      )}
      {sektion ? (
        // B21: Begriffe der Sektion — „Bisher bezahlt" als Bündel (Zahler, Ø Alter) wie im Kopf, kein CPA-Wert
        // (Abrechnungsgröße, P1-V3 § 2.8 entfernt).
        <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
          <strong>{formatKpi('orders', totals.orders)} Anfos</strong> · davon {formatKpi('orders', totals.orders_with_registration)} mit Anmeldung ·{' '}
          {formatKpi('orders', totals.attendee_orders)} von Live-Teilnehmern
          {(totals.orders_provisional ?? 0) > 0 && ` · ${formatKpi('orders', totals.orders_provisional ?? 0)} vorläufig`}
          {d?.paid_revenue !== undefined && (
            <>
              {' · '}
              {TERMS.umsatzNetto.label}{' '}
              <Box component="span" sx={{ display: 'inline-block', verticalAlign: 'top' }}>
                <BezahltBuendel wert={formatEur(d.paid_revenue, 0)} zahler={d.payers ?? 0} alterTage={d.avg_age_days ?? null} />
              </Box>
            </>
          )}
        </Typography>
      ) : (
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        <strong>{formatKpi('orders', totals.orders)} Anfos</strong> · davon{' '}
        {formatKpi('orders', totals.orders_with_registration)} mit Anmeldung ·{' '}
        {formatKpi('orders', totals.attendee_orders)} von Live-Teilnehmern · {KPI.cpa_revenue.label}{' '}
        {formatKpi('cpa_revenue', totals.cpa_revenue)} · {KPI.paid_revenue.label}{' '}
        {formatKpi('paid_revenue', totals.paid_revenue)}
        {totals.orders_without_cpa > 0 && ` · ${totals.orders_without_cpa} ohne CPA-Wert`}
      </Typography>
      )}
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 640 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {head('ordernumber', 'Bestellnummer')}
              {head(
                'email',
                'E-Mail',
                'Käufer-E-Mail aus PLX. Fehlt sie, hat PLX sie noch nicht geliefert.',
              )}
              {head('created_on', 'Bestellt am')}
              {head(
                'vertrag_sts',
                'Status',
                sektion ? 'Vertragsstatus in PL-X: läuft, beendet oder Widerruf.' : 'PLX-Vertragsstatus (L = laufend, B = beendet, W = Widerruf).',
              )}
              {head('product_name', 'Produkt')}
              {head(
                'mapping_method',
                'Zuordnung',
                'Wie die Bestellung diesem Webinar zugeordnet wurde.',
              )}
              {head(
                'affiliate',
                'Kanal (Affiliate)',
                'Affiliate der ERSTEN Anmeldung des Käufers zu diesem Webinar. Ohne Anmeldung steht hier der Grund.',
              )}
              {head('adref', 'Adref')}
              {head(
                'registration_date',
                'Angemeldet am',
                'Zeitpunkt der ersten Anmeldung des Käufers.',
              )}
              {head('attended', 'Live dabei', 'War der Käufer live im Webinar?')}
              {head(
                'customer_status',
                'Vorher',
                'Kundenstatus vor dieser Anfo: Erstbesteller, früher bestellt und nie gezahlt, oder früher gezahlt. Maßgeblich sind frühere Bestellungen derselben Adresse, mindestens 2 h vorher, ohne Stornos und Testbestellungen; „gezahlt“ nach heutigem Stand.',
              )}
              {head('werbeaktion_wbkz', sektion ? 'Werbeaktion' : 'WKZ')}
              {head('banner', 'Banner')}
              {!sektion && head('cpa', KPI.cpa_revenue.short, KPI.cpa_revenue.tooltip, KPI_COLORS.cpa)}
              {/* Sektion: die Spalte ist die Hochrechnung je Anfo (o.ltv) — so heißt sie auch im Kopf. */}
              {sektion
                ? head('ltv', TERMS.hochrechnung.short, TERMS.hochrechnung.tip, KPI_COLORS.ltv)
                : head('ltv', KPI.ltv_revenue.short, KPI.ltv_revenue.tooltip, KPI_COLORS.ltv)}
              {sektion
                ? head('paid', TERMS.umsatzNetto.label, TERMS.umsatzNetto.tip, KPI_COLORS.paid)
                : head('paid', KPI.paid_revenue.short, KPI.paid_revenue.tooltip, KPI_COLORS.paid)}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((o) => (
              <TableRow key={o.ordernumber}>
                <TableCell sx={CELL}>{o.ordernumber}</TableCell>
                <TableCell sx={CELL}>{o.email ?? '–'}</TableCell>
                <TableCell sx={CELL}>
                  {formatGermanTime(o.created_on, 'DD.MM.YYYY, HH:mm')}
                </TableCell>
                <TableCell sx={CELL}>{(sektion ? STATUS_WORT[o.vertrag_sts ?? ''] : null) ?? o.vertrag_sts ?? '–'}</TableCell>
                <TableCell sx={CELL}>{o.product_name || o.product_code || '–'}</TableCell>
                <TableCell sx={CELL}>
                  {methodChip(
                    o.mapping_method,
                    [
                      o.rid && `RID: ${o.rid}`,
                      o.registered_email && `Anmelde-E-Mail: ${o.registered_email}`,
                    ]
                      .filter(Boolean)
                      .join(' · ') || undefined,
                  )}
                </TableCell>
                <TableCell sx={CELL}>
                  {o.has_registration ? (
                    o.affiliate || '(kein Affiliate)'
                  ) : (
                    <Box
                      component="span"
                      sx={{ display: 'inline-flex', alignItems: 'center', color: '#8a6d3b' }}
                    >
                      {o.no_reg_label}
                      {o.no_reg_label && CATEGORY_HINTS[o.no_reg_label] && (
                        <InfoTip text={CATEGORY_HINTS[o.no_reg_label]} />
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={CELL}>{o.adref || '–'}</TableCell>
                <TableCell sx={CELL}>
                  {o.registration_date
                    ? formatGermanTime(o.registration_date, 'DD.MM.YYYY, HH:mm')
                    : '–'}
                </TableCell>
                <TableCell sx={CELL}>{o.has_registration ? yesNo(o.attended) : '–'}</TableCell>
                <TableCell sx={CELL}>
                  {o.customer_status ? CUSTOMER_STATUS_LABELS[o.customer_status] : '–'}
                </TableCell>
                <TableCell sx={CELL}>{o.werbeaktion_wbkz ?? '–'}</TableCell>
                <TableCell sx={CELL}>{o.banner ?? '–'}</TableCell>
                {!sektion && (
                <TableCell sx={{ ...CELL, color: KPI_COLORS.cpa }} align="right">
                  {o.cpa === null ? (
                    <Tooltip
                      title="Kein CPA-Wert für dieses Produkt (oder Artikel noch nicht geliefert) — nicht im Umsatz enthalten."
                      arrow
                    >
                      <span style={{ cursor: 'help' }}>– *</span>
                    </Tooltip>
                  ) : (
                    formatKpi('avg_cpa_per_order', o.cpa)
                  )}
                </TableCell>
                )}
                <TableCell sx={{ ...CELL, color: KPI_COLORS.ltv }} align="right">
                  {/* Codex-Befund 26.10.: die Summe im Kopf schließt vorläufige aus — die Zeile darf keinen Wert zeigen. */}
                  {sektion && Number(o.is_provisional) === 1 ? (
                    <Tooltip title={TERMS.vorlaeufig.tip} arrow>
                      <span style={{ cursor: 'help', color: '#8a8982' }}>— (vorläufig, ohne Wert)</span>
                    </Tooltip>
                  ) : (
                    formatKpi('avg_ltv_per_order', o.ltv)
                  )}
                </TableCell>
                <TableCell sx={{ ...CELL, color: KPI_COLORS.paid }} align="right">
                  {formatKpi('avg_paid_per_order', o.paid)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const RegistrationsTab: FC<{ rows: WebinarRegistrationData[]; sektion?: boolean; e?: WebinarEngagementDetailData }> = ({ rows, sektion = false, e }) => {
  const { sorted, head } = useSortedRows(rows, 'registration_date');
  const persons = rows.filter((r) => r.is_first_registration);
  const buyers = persons.filter((r) => r.has_order).length;
  const timely = persons.filter((r) => r.post_start === 0).length;
  const zeitpunkt = (ps: number) =>
    sektion
      ? ps === 0
        ? 'vor Beginn'
        : ps === 3
          ? 'eingestiegen'
          : 'zu spät für live'
      : ps === 0
        ? 'vor Start'
        : ps === 3
          ? 'Späteinlass'
          : 'nach Start';
  return (
    <Box>
      {sektion && e && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>
            {e.registrations.toLocaleString('de-DE')} Live-Anmeldungen ({e.timely.toLocaleString('de-DE')} vor Beginn,{' '}
            {Math.max(0, e.registrations - e.timely).toLocaleString('de-DE')} während des Webinars eingestiegen)
          </strong>
          {(e.registrations_after_start ?? 0) > 0 && ` · +${(e.registrations_after_start ?? 0).toLocaleString('de-DE')} zu spät für live`}
          {` · ${persons.length.toLocaleString('de-DE')} Personen`}
        </Typography>
      )}
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        <strong>{persons.length.toLocaleString('de-DE')} Personen</strong> (
        {rows.length.toLocaleString('de-DE')} Anmeldezeilen — manche Personen haben sich mehrfach
        angemeldet) · {timely.toLocaleString('de-DE')} rechtzeitig ·{' '}
        {buyers.toLocaleString('de-DE')} Personen mit Anfo (
        {persons.length ? ((buyers * 100) / persons.length).toFixed(1).replace('.', ',') : '–'} %
        der Personen)
        <InfoTip text="Die Kacheln zählen Personen, nicht Anmeldezeilen. „Personen mit Anfo“ ist ein Personen-Anteil und deshalb nicht dasselbe wie die Reg.-Conversion (Anfos ÷ Registrierungen): eine Person kann mehrere Anfos haben." />
      </Typography>
      <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', maxHeight: 640 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {head('email', 'E-Mail')}
              {head('registration_date', 'Angemeldet am')}
              {head(
                'post_start',
                'Zeitpunkt',
                'Vor oder nach Sendestart angemeldet. Nach Start = Aufzeichnung, kann nicht live dabei sein.',
              )}
              {head(
                'is_first_registration',
                'Anmeldung',
                '„Erste“ = diese Zeile bestimmt Kanal und Gerät der Person. Weitere Anmeldungen derselben Person zählen nicht erneut.',
              )}
              {head('affiliate', 'Affiliate')}
              {head('adref', 'Adref')}
              {head('rid', 'RID')}
              {head('signup_device', 'Gerät', 'Gerät beim Absenden des Anmeldeformulars.')}
              {head('attended', 'Live dabei')}
              {head(
                'first_signup_date',
                'Erster Signup',
                'Wann die E-Mail-Adresse zum ersten Mal bei uns auftauchte.',
              )}
              {head(
                'days_to_register',
                'Tage bis Anm.',
                'Tage zwischen erstem Signup und dieser Anmeldung.',
              )}
              {head('has_order', 'Anfo', 'Hat diese Person eine Anfo zu diesem Webinar?')}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((r) => (
              <TableRow
                key={r.id}
                sx={{ backgroundColor: r.is_first_registration ? undefined : '#fafafa' }}
              >
                <TableCell sx={CELL}>{r.email}</TableCell>
                <TableCell sx={CELL}>
                  {formatGermanTime(r.registration_date, 'DD.MM.YYYY, HH:mm')}
                </TableCell>
                <TableCell sx={CELL}>
                  <Chip
                    size="small"
                    label={zeitpunkt(r.post_start)}
                    sx={{
                      backgroundColor:
                        r.post_start === 0 ? '#e8f5e9' : r.post_start === 3 ? '#e3f2fd' : '#fff3e0',
                    }}
                  />
                </TableCell>
                <TableCell sx={CELL}>
                  {r.is_first_registration
                    ? r.registrations_for_email > 1
                      ? `erste von ${r.registrations_for_email}`
                      : 'einzige'
                    : 'weitere'}
                </TableCell>
                <TableCell sx={CELL}>{r.affiliate || '–'}</TableCell>
                <TableCell sx={CELL}>{r.adref || '–'}</TableCell>
                <TableCell sx={CELL}>{r.rid || '–'}</TableCell>
                <TableCell sx={CELL}>{r.signup_device}</TableCell>
                <TableCell sx={CELL}>{yesNo(r.attended)}</TableCell>
                <TableCell sx={CELL}>
                  {r.first_signup_date
                    ? new Date(r.first_signup_date).toLocaleDateString('de-DE')
                    : '–'}
                </TableCell>
                <TableCell sx={CELL}>{r.days_to_register ?? '–'}</TableCell>
                <TableCell sx={CELL}>
                  <Chip
                    size="small"
                    label={r.has_order ? 'Ja' : 'Nein'}
                    sx={{ backgroundColor: r.has_order ? '#c8e6c9' : '#eeeeee' }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const MappingTab: FC<{ data: WebinarMappingBreakdownData; colors: ReturnType<typeof tokens> }> = ({
  data,
  colors,
}) => {
  // Mobil reicht der Rand nicht fuer Linienbeschriftungen; die Tabelle direkt darunter nennt alle Wege.
  const schmal = useMediaQuery(useTheme().breakpoints.down('sm'));
  const leading: ExtraColumn<MappingBreakdownItem>[] = [
    {
      id: 'mapping_method',
      label: 'Zuordnung',
      tooltip: 'Wie die Bestellungen diesem Webinar zugeordnet wurden.',
      sortValue: (r) => r.mapping_method,
      render: (r) => methodChip(r.mapping_method),
    },
    {
      id: 'percentage',
      label: 'Anteil',
      tooltip: 'Anteil an allen Anfos dieses Webinars.',
      sortValue: (r) => r.percentage,
      render: (r) => `${r.percentage.toLocaleString('de-DE')} %`,
    },
  ];
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))',
        gap: 3,
      }}
    >
      <Paper sx={{ p: 3, backgroundColor: colors.primary[400], height: 400 }}>
        <Typography variant="h6" sx={{ color: colors.grey[100], mb: 2 }}>
          Anfos nach Zuordnungsweg
        </Typography>
        <Box sx={{ height: 300 }}>
          <ResponsivePie
            data={data.breakdown.map((b) => ({
              id: MAPPING_METHODS[b.mapping_method]?.label ?? b.mapping_method,
              label: MAPPING_METHODS[b.mapping_method]?.kurz ?? MAPPING_METHODS[b.mapping_method]?.label ?? b.mapping_method,
              value: b.orders,
              color: MAPPING_METHODS[b.mapping_method]?.color ?? '#757575',
            }))}
            colors={{ datum: 'data.color' }}
            margin={{ top: 20, right: 90, bottom: 20, left: 90 }}
            innerRadius={0.5}
            padAngle={0.7}
            cornerRadius={3}
            arcLinkLabel="label"
            // Schmale Stuecke ohne Linienbeschriftung, sonst ueberlappen sie; die Tabelle daneben nennt alle.
            arcLinkLabelsSkipAngle={12}
            enableArcLinkLabels={!schmal}
            arcLinkLabelsTextColor={colors.grey[100]}
            arcLabelsTextColor="#ffffff"
          />
        </Box>
      </Paper>
      <Box>
        <MetricsTable
          rows={data.breakdown}
          leading={leading}
          metrics={['orders', 'cpa_revenue', 'paid_revenue']}
          rowKey={(r) => r.mapping_method}
          defaultSortBy="orders"
        />
      </Box>
    </Box>
  );
};

/**
 * Woher die Detailseite ihre Daten holt und wohin „Zurück" führt. Zwei Quellen mit denselben
 * Service-Aufrufen: die alte Seite /webinar-auswertungen/:id über /api/webinar-statistics (Gruppe
 * webinars_mailings) und die Sektion /auswertungen/webinare/:id über /api/auswertungen (fit_products).
 * Die alte Seite bleibt bis zur Abnahme der Sektion unverändert erreichbar.
 */
export interface WebinarDetailQuelle {
  engagement: (webinarId: string) => Promise<WebinarEngagementDetailData>;
  anfos: (webinarId: string) => Promise<WebinarOrderDetailData[]>;
  registrierungen: (webinarId: string) => Promise<WebinarRegistrationData[]>;
  zuordnung: (webinarId: string) => Promise<WebinarMappingBreakdownData>;
  gruppierung: (webinarId: string) => Promise<WebinarGroupedEvaluationDetailData>;
  /** Geschätzte Zuschauerkurve der Erstausstrahlung im eigenen Raum. */
  raum: (webinarId: string) => Promise<RoomRetentionData>;
  /** Vergleich mit den letzten Webinaren + Herkunft der ANFOs ohne Teilnahme (wie die Montagsmail). */
  vergleich: (webinarId: string) => Promise<WebinarComparisonData>;
  /** Bezugsart (nur Sektion, P3-Nachzug): ohne sie fehlt der Block — die alte Seite bleibt, wie sie ist. */
  bezugsart?: (webinarId: string) => Promise<BezugsartAntwort>;
  zurueck: string;
  /** Geldbasis der Quelle: die alte Seite rechnet brutto, die Sektion /auswertungen netto (seit 08.10.2026). */
  geldbasis: 'brutto' | 'netto';
  /**
   * EINZIGER Schalter der Sektions-Darstellung (P1-V3 § 2.8): Kachel-Reihen, Tab-Reihenfolge und Begriffe der Sektion.
   * Ohne ihn (alte Seite /webinar-auswertungen/:id) bleibt alles wie vorher.
   */
  sektion?: boolean;
  /** Termin-Kennzahlen aus derselben Rechnung wie die Webinar-Tabelle (K6); nur Sektion. */
  kennzahlen?: (webinarId: string) => Promise<DetailKennzahlen>;
  /** Show-up je Termin der Kapitelseite — nur für „—" bei Terminen ohne belastbare Teilnahme im Vergleich (B20). */
  teilnahme?: () => Promise<ShowUpAntwort>;
  /** „Typisch" der Kacheln und Kundentyp-Schnitt aus den 12 Monaten VOR dem Termin (nur Sektion, Plan 2026-10-30). */
  typisch?: (webinarId: string) => Promise<TypischVorTermin | null>;
}

type TabKey = 'uebersicht' | 'anfos' | 'registrierungen' | 'zuordnung' | 'gruppierung';
/** Tabs per Schlüssel statt Index (`.claude/rules/react-frontend.md` § Adding a TAB); Reihenfolge je Darstellung. */
const TABS_ALT: { key: TabKey; label: string; icon: JSX.Element }[] = [
  { key: 'uebersicht', label: 'Übersicht', icon: <DashboardTwoTone /> },
  { key: 'anfos', label: 'Anfos', icon: <ShoppingCartTwoTone /> },
  { key: 'registrierungen', label: 'Registrierungen', icon: <PersonAddTwoTone /> },
  { key: 'zuordnung', label: 'Mapping-Analyse', icon: <AccountTreeTwoTone /> },
  { key: 'gruppierung', label: 'Gruppierung', icon: <CategoryTwoTone /> },
];
const TABS_SEKTION: { key: TabKey; label: string; icon: JSX.Element }[] = [
  { key: 'uebersicht', label: 'Übersicht', icon: <DashboardTwoTone /> },
  { key: 'anfos', label: 'Anfos', icon: <ShoppingCartTwoTone /> },
  { key: 'registrierungen', label: 'Anmeldungen', icon: <PersonAddTwoTone /> },
  { key: 'gruppierung', label: 'Woher die Anmeldungen kamen', icon: <CategoryTwoTone /> },
  { key: 'zuordnung', label: 'Zuordnung der Bestellungen', icon: <AccountTreeTwoTone /> },
];

/** Restzeile der Gerätetabelle; Text aus dem Backend (`node-app/src/helpers/webinarFacts.ts` POST_START_ORDER_LABEL). */
const KAUF_NACH_START_OHNE_ANMELDUNG = 'Kauf nach Sendestart, ohne Anmeldung';

/** Gerätetabelle der Sektion: nur nach Anmeldegerät, sechs Spalten, Begriffe der Sektion (P1-V3 § 2.8, K4). */
const SektionGeraete: FC<{ rows: EvaluationMetrics[]; showUpGrund?: string | null }> = ({ rows, showUpGrund = null }) => {
  // B47: dieselbe Entscheidung wie der Kopf — bei Messlücke/Tracking-Warnung/übernommen „—" mit Grund statt Rohquote.
  const showUpText = (r: EvaluationMetrics) => (showUpGrund || r.show_up_rate === null ? '–' : `${formatNumber(r.show_up_rate, 1)} %`);
  const anz = (v: number, eins: string, viele: string) => `${v.toLocaleString('de-DE')} ${v === 1 ? eins : viele}`;
  // Keine Spaltenwahl hier: schmal (< 640 px) stehen Anmeldungen, Show-up und Anfos in der Gerätezelle (Browser-Abnahme
  // 25.10.: 6 Spalten liefen bei 269 px auf 629 px), unter 860 px enge Zellen.
  const [breiteRef, breitePx] = useBreitePx();
  const schmal = breitePx !== null && breiteAus(breitePx) === 'schmal';
  const unter = (s: string) => (
    <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', whiteSpace: 'normal' }}>
      {s}
    </Box>
  );
  const alle: Spalte<EvaluationMetrics>[] = [
    {
      key: 'geraet',
      label: 'Anmeldegerät',
      render: (r) => (
        <Box sx={{ lineHeight: 1.3 }}>
          {r.category}
          {schmal &&
            unter(
              // B32: Singular, und ohne Anmeldungen kein „Show-up –".
              [`${r.registrations.toLocaleString('de-DE')} Anm.`, r.registrations > 0 ? `Show-up ${showUpText(r)}` : null, anz(r.orders, 'Anfo', 'Anfos')]
                .filter(Boolean)
                .join(' · '),
            )}
        </Box>
      ),
      sortValue: (r) => r.category,
    },
    { key: 'anm', label: TERMS.anmeldungenLive.label, tip: TERMS.anmeldungenLive.tip, align: 'right', render: (r) => r.registrations.toLocaleString('de-DE'), sortValue: (r) => r.registrations },
    {
      key: 'showup',
      label: TERMS.showUpTermin.label,
      einheit: TERMS.showUpTermin.einheit,
      tip: TERMS.showUpTermin.tip,
      align: 'right',
      render: (r) =>
        showUpGrund ? (
          <Tooltip title={showUpGrund} arrow placement="top">
            <Box component="span" sx={{ cursor: 'help' }}>
              —
            </Box>
          </Tooltip>
        ) : (
          showUpText(r)
        ),
      sortValue: (r) => (showUpGrund ? null : r.show_up_rate),
    },
    { key: 'anfos', label: TERMS.anfos.label, tip: TERMS.anfos.tip, align: 'right', render: (r) => r.orders.toLocaleString('de-DE'), sortValue: (r) => r.orders },
    {
      key: 'a100',
      label: TERMS.anfosJe100Anm.label,
      tip: TERMS.anfosJe100Anm.tip,
      align: 'right',
      // Zähler ohne vorläufige (Regel 13, Codex-Befund 25.10.).
      render: (r) => (r.registrations > 0 ? formatNumber(((r.orders - (r.orders_provisional ?? 0)) * 100) / r.registrations, 1) : '–'),
      sortValue: (r) => (r.registrations > 0 ? (r.orders - (r.orders_provisional ?? 0)) / r.registrations : null),
    },
    {
      // `attendee_orders` zählt BESTELLUNGEN, nicht Käufer — als Anteil „Käufer" wären über 100 % möglich. Ehrlich
      // beschriftet als „Anfos je 100 Teilnehmer" (TERMS.anfosJeTeilnehmer, Codex-Befund 25.10.).
      key: 'a100t',
      label: TERMS.anfosJeTeilnehmer.je100Label,
      einheit: 'Anfos von Teilnehmern',
      tip: TERMS.anfosJeTeilnehmer.tip,
      align: 'right',
      // Ohne belastbare Teilnahme (Messlücke, übernommene Anmeldungen, Tracking-Warnung) wie der Show-up „–" — sonst
      // stünde hier eine Teilnehmer-Quote, die die Anfo-Kachel oben ausdrücklich nicht aufteilt (Review 27.10.).
      render: (r) =>
        showUpGrund ? (
          <Tooltip title={showUpGrund} arrow placement="top">
            <span>–</span>
          </Tooltip>
        ) : (
          <Box sx={{ lineHeight: 1.25 }}>
            {r.attendees > 0 ? formatNumber((r.attendee_orders * 100) / r.attendees, 1) : '–'}
            {unter(`${r.attendee_orders.toLocaleString('de-DE')} von ${r.attendees.toLocaleString('de-DE')}`)}
          </Box>
        ),
      sortValue: (r) => (showUpGrund || r.attendees <= 0 ? null : r.attendee_orders / r.attendees),
    },
  ];
  const spalten = schmal ? alle.filter((s) => ['geraet', 'a100', 'a100t'].includes(s.key)) : alle;
  // Persona-Umbau B9: nur mit mindestens der Hälfte bekannter Geräte — sonst ist die Tabelle fast nur „Unbekannt"
  // (KCV8vu: Unbekannt 431 von 431).
  const anmeldungen = rows.reduce((s, r) => s + r.registrations, 0);
  const unbekannt = rows.filter((r) => r.category === 'Unbekannt').reduce((s, r) => s + r.registrations, 0);
  if (anmeldungen > 0 && (anmeldungen - unbekannt) * 2 < anmeldungen)
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          Nach Anmeldegerät
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
          Gerät bei diesem Termin nicht erfasst: {(anmeldungen - unbekannt).toLocaleString('de-DE')} von {anmeldungen.toLocaleString('de-DE')} Anmeldungen mit
          bekanntem Gerät.
          <InfoTip text={TERMS.geraetNichtErfasst.tip} />
        </Typography>
      </Box>
    );
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
        Nach Anmeldegerät
      </Typography>
      <Box ref={breiteRef}>
        <SortableTable
          rows={rows}
          columns={spalten}
          rowKey={(r) => r.category}
          initialSort={{ key: 'anm', dir: 'desc' }}
          // Die Restzeile ist kein Gerät — beim Sortieren immer am Ende (Bedienprüfung 28.10.).
          abschnitt={(r) => (r.category === KAUF_NACH_START_OHNE_ANMELDUNG ? '' : null)}
          maxHeight={null}
          eng={breitePx !== null && breitePx < ENG_UNTER}
        />
      </Box>
    </Box>
  );
};

const WebinarDetail: FC<{ quelle: WebinarDetailQuelle }> = ({ quelle }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { webinarId } = useParams<{ webinarId: string }>();

  const sektion = quelle.sektion === true;
  const tabs = sektion ? TABS_SEKTION : TABS_ALT;
  const [tabValue, setTabValue] = useState<TabKey>('uebersicht');
  const [kennzahlen, setKennzahlen] = useState<DetailKennzahlen | null>(null);
  const [loading, setLoading] = useState(true);
  const [engagement, setEngagement] = useState<WebinarEngagementDetailData | null>(null);
  const [orders, setOrders] = useState<WebinarOrderDetailData[] | null>(null);
  const [registrations, setRegistrations] = useState<WebinarRegistrationData[] | null>(null);
  const [mapping, setMapping] = useState<WebinarMappingBreakdownData | null>(null);
  const [grouped, setGrouped] = useState<WebinarGroupedEvaluationDetailData | null>(null);
  const [raum, setRaum] = useState<RoomRetentionData | null>(null);
  const [vergleich, setVergleich] = useState<WebinarComparisonData | null>(null);
  const [bezugsart, setBezugsart] = useState<BezugsartAntwort | null>(null);
  const [ohneTeilnahme, setOhneTeilnahme] = useState<Record<string, string> | undefined>(undefined);
  // „typisch" der Kacheln und Kundentyp-Schnitt: die 12 Monate VOR diesem Termin (fail-soft, nicht blockierend). Je Termin
  // neu: beim Wechsel zuerst leeren, damit nie die Werte des vorigen Termins stehen; späte Antworten verwerfen (Codex 30.10.).
  const [typischVor, setTypischVor] = useState<TypischVorTermin | null>(null);
  useEffect(() => {
    setTypischVor(null);
    if (!quelle.typisch || !webinarId) return;
    let cancelled = false;
    quelle
      .typisch(webinarId)
      .then((t) => !cancelled && setTypischVor(t))
      .catch((err) => console.error('Error loading typisch:', err));
    return () => {
      cancelled = true;
    };
  }, [quelle, webinarId]);
  const typisch: TypischDetail | null = useMemo(
    () => (typischVor?.typisch ? { ...typischVor.typisch, show_up_termine: typischVor.show_up_termine } : null),
    [typischVor],
  );
  const [tabLoading, setTabLoading] = useState(false);

  // B20: Gründe „keine belastbare Teilnahme" je Termin (Kapitelseite, `showUpZeile`) — einmal je Seitenaufruf.
  useEffect(() => {
    if (!quelle.teilnahme) return;
    let cancelled = false;
    quelle
      .teilnahme()
      .then((t) => {
        if (cancelled) return;
        const m: Record<string, string> = {};
        for (const r of t.rows) if (r.grund) m[r.webinar_id] = SHOWUP_GRUND[r.grund];
        setOhneTeilnahme(m);
      })
      .catch((e) => console.error('Error loading teilnahme:', e));
    return () => {
      cancelled = true;
    };
  }, [quelle]);

  useEffect(() => {
    if (!webinarId) return;
    let cancelled = false;
    setLoading(true);
    setEngagement(null);
    setOrders(null);
    setRegistrations(null);
    setMapping(null);
    setGrouped(null);
    setRaum(null);
    setVergleich(null);
    setBezugsart(null);
    setKennzahlen(null);
    quelle
      .kennzahlen?.(webinarId)
      .then((d) => !cancelled && setKennzahlen(d))
      .catch((e) => console.error('Error loading kennzahlen:', e));
    quelle
      .engagement(webinarId)
      .then((d) => !cancelled && setEngagement(d))
      .catch((e) => console.error('Error loading engagement data:', e))
      .finally(() => !cancelled && setLoading(false));
    // Parallel und ohne die Seite aufzuhalten: die Kurve ist nur im Übersicht-Tab.
    quelle
      .raum(webinarId)
      .then((d) => !cancelled && setRaum(d))
      .catch((e) => console.error('Error loading room retention:', e));
    quelle
      .vergleich(webinarId)
      .then((d) => !cancelled && setVergleich(d))
      .catch((e) => console.error('Error loading comparison:', e));
    quelle
      .bezugsart?.(webinarId)
      .then((d) => !cancelled && setBezugsart(d))
      .catch((e) => console.error('Error loading bezugsart:', e));
    return () => {
      cancelled = true;
    };
  }, [webinarId, quelle]);

  useEffect(() => {
    if (!webinarId) return;
    const loaders: Partial<Record<TabKey, [boolean, () => Promise<void>]>> = {
      anfos: [orders !== null, async () => setOrders(await quelle.anfos(webinarId))],
      registrierungen: [
        registrations !== null,
        async () => setRegistrations(await quelle.registrierungen(webinarId)),
      ],
      zuordnung: [mapping !== null, async () => setMapping(await quelle.zuordnung(webinarId))],
      gruppierung: [
        grouped !== null,
        async () => setGrouped(await quelle.gruppierung(webinarId)),
      ],
    };
    const entry = loaders[tabValue];
    if (!entry || entry[0]) return;
    setTabLoading(true);
    entry[1]()
      .catch((e) => console.error('Error loading tab data:', e))
      .finally(() => setTabLoading(false));
    // Nur Tab-Wechsel und Webinar loesen Laden aus; die Datenzustaende sind die Abbruchbedingung.
  }, [tabValue, webinarId, quelle]);

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!engagement) {
    return (
      <Box m="20px">
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(quelle.zurueck)}
          sx={{ mb: 2 }}
        >
          Zurück zur Übersicht
        </Button>
        <Typography variant="h5">Webinar nicht gefunden</Typography>
      </Box>
    );
  }

  const e = engagement;
  const n = (v: number) => v.toLocaleString('de-DE');
  // Sektions-Sicht (netto): `registrations` zählt nur Live-Anmeldungen (rechtzeitig oder Späteinlass), wer
  // sich nur nach Sendestart angemeldet hat, steht in `registrations_after_start` (Till 14.10.2026). Die
  // Differenz zu „rechtzeitig“ ist dort also der Späteinlass; auf der alten Seite alles nach Sendestart.
  const netto = quelle.geldbasis === 'netto';
  const late = e.registrations - e.timely;
  const nachStart = e.registrations_after_start ?? 0;
  const vorlaeufig = e.orders_provisional ?? 0;
  const registrierungSub = netto
    ? [late > 0 ? `davon ${n(late)} per Späteinlass` : null, nachStart > 0 ? `plus ${n(nachStart)} nach Sendestart angemeldet` : null]
        .filter(Boolean)
        .join(' · ') || undefined
    : late > 0
      ? `davon ${n(late)} nach Sendestart`
      : undefined;
  const typeLabel =
    { automated: 'Automated', live: 'Live', hybrid: 'Hybrid', replay: 'Replay' }[e.type] || e.type;

  return (
    <Box m="20px">
      {/* Schmal: Zurück über dem Titel — daneben quetscht ein langer Webinartitel den Knopf. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          mb: 2,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(quelle.zurueck)}
          variant="outlined"
          sx={{ flexShrink: 0 }}
        >
          Zurück
        </Button>
        {sektion ? (
          <Header
            title={kennzahlen?.public_title ?? e.title}
            subtitle={[
              kennzahlen?.plx_product,
              `${['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][new Date(`${String(e.start_date).slice(0, 10)}T12:00:00Z`).getUTCDay()]} ${formatGermanTime(e.start_date, 'DD.MM.YYYY, HH:mm')}`,
              raum?.room ? 'eigener Webinarraum' : null,
              kennzahlen && kennzahlen.public_title !== e.title ? `intern: ${e.title}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          />
        ) : (
          <Header
            title={e.title}
            subtitle={`${typeLabel} Webinar - ${formatGermanTime(e.start_date, 'D.M.YYYY HH:mm')} Uhr`}
          />
        )}
      </Box>
      <Typography variant="body2" sx={{ mt: -1, mb: 2, color: 'text.secondary' }}>
        {sektion
          ? 'Alle Euro-Werte netto (ohne MwSt.), außer dem Listenpreis der Werbeaktion. Der Erwartete Wert bewertet, dass bestellt wurde — mit dem heutigen Wert des Angebots, auch für ältere Termine. Die Hochrechnung aus den bisherigen Zahlungen steht nur, wo sie so heißt.'
          : `Alle Euro-Werte ${quelle.geldbasis === 'netto' ? 'netto (ohne MwSt.)' : 'brutto (inkl. MwSt.)'}. Seit 13.10.2026 trägt jede Anfo ihren eigenen Wert: bis ihre Zahlung zählt den ihres Angebots, danach den gezahlten Betrag hochgerechnet; nie bezahlte zählen 0 €. Die Werte ändern sich deshalb täglich.`}
      </Typography>

      {e.tracking_suspect && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Tracking-Warnung:</strong> StealthSeminar hat für dieses Webinar kaum
          Teilnahme-Signale geliefert ({n(e.attendees)} Teilnehmer bei {n(e.timely)} rechtzeitig
          Angemeldeten). Show-up-Rate und Teilnehmer-Conversion sind hier nicht belastbar.
        </Alert>
      )}

      {sektion ? (
        <SektionKopf e={e} d={kennzahlen} raum={raum} typisch={typisch} />
      ) : (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(230px, 100%), 1fr))',
          gap: '16px',
          mb: '24px',
        }}
      >
        <KpiTile
          metric="registrations"
          value={e.registrations}
          icon={<RegistrationsIcon sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
          sub={registrierungSub}
          tooltipOverride={netto ? TERMS.registrierungen.tip : undefined}
        />
        <KpiTile
          metric="timely"
          value={e.timely}
          icon={<PersonAddTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
        />
        <KpiTile
          metric="attendees"
          value={e.attendees}
          icon={<GroupsTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
        />
        <KpiTile
          metric="show_up_rate"
          value={e.show_up_rate}
          icon={<TrendingUpTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
          sub={`${n(e.attendees)} von ${n(e.timely)} rechtzeitig Angemeldeten`}
        />
        <KpiTile
          metric="orders"
          value={e.orders}
          icon={<ShoppingCartTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
          sub={`${n(e.orders_with_registration)} mit Anmeldung · ${n(e.attendee_orders)} von Teilnehmern${
            vorlaeufig > 0 ? ` · ${n(vorlaeufig)} vorläufig ergänzt` : ''
          }`}
        />
        <KpiTile
          metric="registration_conversion_rate"
          value={e.registration_conversion_rate}
          sub={`${n(e.orders)} Anfos von ${n(e.registrations)} Registrierungen`}
        />
        <KpiTile
          metric="attendee_conversion_rate"
          value={e.attendee_conversion_rate}
          sub={`${n(e.attendee_orders)} Anfos von ${n(e.attendees)} Teilnehmern`}
        />
        <KpiTile
          metric="cpa_revenue"
          value={e.cpa_revenue}
          icon={<EuroTwoTone sx={{ fontSize: 36, color: KPI.cpa_revenue.color }} />}
          sub={
            e.orders_without_cpa
              ? `${n(e.orders_without_cpa)} ${Number(e.orders_without_cpa) === 1 ? 'Anfo' : 'Anfos'} ohne CPA-Wert nicht enthalten`
              : undefined
          }
        />
        <KpiTile metric="cpa_per_registration" value={e.cpa_per_registration} />
        <KpiTile metric="cpa_per_attendee" value={e.cpa_per_attendee} />
        <KpiTile metric="ltv_revenue" value={e.ltv_revenue} />
        <KpiTile metric="paid_revenue" value={e.paid_revenue} />
        <KpiTile
          metric="cta_ctr"
          value={e.cta_ctr}
          icon={<TouchAppTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
          sub={`${n(e.cta_clicks)} von ${n(e.cta_views)} haben geklickt`}
        />
        {e.cta_buy_rate != null && (
          <KpiTile
            metric="cta_buy_rate"
            value={e.cta_buy_rate}
            icon={<ShoppingCartTwoTone sx={{ fontSize: 36, color: colors.greenAccent[500] }} />}
            sub={`${n(e.cta_buyers ?? 0)} von ${n(e.cta_clicks)} Klickern haben bestellt`}
          />
        )}
      </Box>
      )}

      <Tabs
        value={tabValue}
        onChange={(_ev, v: TabKey) => setTabValue(v)}
        sx={{ mb: 3 }}
        variant="scrollable"
      >
        {tabs.map((tb) => (
          <Tab key={tb.key} value={tb.key} icon={tb.icon} iconPosition="start" label={tb.label} />
        ))}
      </Tabs>

      {tabLoading && tabValue !== 'uebersicht' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {tabValue === 'uebersicht' && sektion && (
        <Box>
          <Explainer
            title="So liest du diese Seite — zum Nachlesen, nichts zu tun"
            sections={KPI_CONCEPTS.filter((c) => !/StealthSeminar|Filter|brutto/i.test(`${c.titel} ${c.text}`)).map((c) => ({ label: c.titel, body: c.text }))}
          />
          {raum?.room && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
              <RoomRetentionChart data={raum.room} sektion />
            </Paper>
          )}
          {vergleich && vergleich.rows.length > 1 && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
              <WebinarComparison data={vergleich} geldbasis={quelle.geldbasis} sektion ohneTeilnahme={ohneTeilnahme} />
            </Paper>
          )}
          {e.buyer_status && e.buyer_status.buyers > 0 && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
              <SektionKaeufer status={e.buyer_status} schnitt={typischVor?.anteil_nie_gezahlt ?? null} />
              {vergleich?.nonAttendeeText && (e.tracking_suspect || kennzahlen?.show_up?.grund) ? (
                // Teilnahme nicht messbar (Messlücke, übernommene Anmeldungen, Tracking-Warnung): keine Aufteilung nach
                // „angemeldet, nicht live" — dieselbe Sperre wie die Anfo-Kachel oben.
                <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                  <strong>Anfos ohne Teilnahme:</strong> nicht auswertbar —{' '}
                  {kennzahlen?.show_up?.grund ? SHOWUP_GRUND[kennzahlen.show_up.grund] : 'die Teilnahme ist für diesen Termin nicht verlässlich gemessen.'}
                </Typography>
              ) : vergleich?.nonAttendeeText && (
                // B32: Sektionsschreibweise „Anfos" (der Satz kommt wortgleich aus der Montagsmail).
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  <strong>{vergleich.nonAttendeeText.head.replace(/ANFOs/g, 'Anfos')}:</strong> {vergleich.nonAttendeeText.text.replace(/ANFOs/g, 'Anfos')}.
                </Typography>
              )}
            </Paper>
          )}
          {bezugsart && bezugsart.anfos > 0 && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
              <BezugsartTabelle data={bezugsart} />
            </Paper>
          )}
          <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
            <SektionGeraete rows={e.signup_devices} showUpGrund={kennzahlen?.show_up?.grund ? SHOWUP_GRUND[kennzahlen.show_up.grund] : null} />
          </Paper>
        </Box>
      )}
      {tabValue === 'uebersicht' && !sektion && (
        <Box>
          <Explainer
            title="So lesen Sie diese Seite"
            sections={KPI_CONCEPTS.map((c) => ({ label: c.titel, body: c.text }))}
          />
          {e.buyer_status && e.buyer_status.buyers > 0 && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
              <BuyerStatusLine status={e.buyer_status} />
              {vergleich?.nonAttendeeText && (
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                  <strong>{vergleich.nonAttendeeText.head}:</strong> {vergleich.nonAttendeeText.text}.
                </Typography>
              )}
            </Paper>
          )}
          {bezugsart && bezugsart.anfos > 0 && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
              <BezugsartTabelle data={bezugsart} />
            </Paper>
          )}
          {raum?.room && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
              <RoomRetentionChart data={raum.room} />
            </Paper>
          )}
          {vergleich && vergleich.rows.length > 1 && (
            <Paper sx={{ p: 2, mb: 2, backgroundColor: '#ffffff' }}>
              <WebinarComparison data={vergleich} geldbasis={quelle.geldbasis} />
            </Paper>
          )}
          <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
            <DeviceFunnel signup={e.signup_devices} attendance={e.attendance_devices} totals={e} />
          </Paper>
        </Box>
      )}
      {tabValue === 'anfos' && !tabLoading && orders && <OrdersTab rows={orders} totals={e} sektion={sektion} d={kennzahlen} />}
      {tabValue === 'registrierungen' && !tabLoading && registrations && <RegistrationsTab rows={registrations} sektion={sektion} e={e} />}
      {tabValue === 'zuordnung' && !tabLoading && mapping && <MappingTab data={mapping} colors={colors} />}
      {tabValue === 'gruppierung' && !tabLoading && grouped && !sektion && <GroupedSections data={grouped} />}
      {tabValue === 'gruppierung' && !tabLoading && grouped && sektion && (
        <SektionHerkunft
          data={grouped}
          showUpGrund={kennzahlen?.show_up?.grund ? SHOWUP_GRUND[kennzahlen.show_up.grund] : e.tracking_suspect ? 'Tracking-Warnung: die Teilnahme ist für diesen Termin nicht verlässlich gemessen.' : null}
          geraete={
            <Paper sx={{ p: 2, backgroundColor: '#ffffff' }}>
              <SektionGeraete rows={grouped.bySignupDevice} showUpGrund={kennzahlen?.show_up?.grund ? SHOWUP_GRUND[kennzahlen.show_up.grund] : null} />
            </Paper>
          }
        />
      )}
    </Box>
  );
};

export default WebinarDetail;
